import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router";
import { RefreshCw, LayoutDashboard, Users, BookOpen, ChevronRight } from "lucide-react";

import styles from "./dashboard.module.scss";
import Card from "../../components/card";
import { useLanguage } from "../../context/languageContext";
import { fetchAllGenresByLangOptions } from "../../helper";
import SessionTimeoutPopup from "../../components/popup/sessionTimeout";

export default function Dashboard() {
    const { adminInfo } = useLanguage();
    const navigate = useNavigate();
    const hasFetched = useRef(false);

    const [dashboardData, setDashboardData] = useState({
        userCount: 0,
        categoryCount: 0,
        recommendersCount: 0,
        booksCount: 0,
        favoriteAuthorsCount: 0,
        collectionsCount: 0,
    });

    const [booksCount, setBooksCount] = useState(0);
    const [genresData, setGenresData] = useState([]);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const [values, setValues] = useState({
        type: "NonFiction",
        category: null,
        isShowType: true,
    });

    const apiUrl = useMemo(() => {
        return axios.create({
            baseURL: process.env.REACT_APP_API_ROOT,
            headers: { Authorization: adminInfo?.token ? `Bearer ${adminInfo.token}` : "" }
        });
    }, [adminInfo?.token]);

    useEffect(() => {
        if (adminInfo && adminInfo.token && !hasFetched.current) {
            document.title = "Penspace - Dashboard";
            initDashboard();
            hasFetched.current = true;
        }
    }, [adminInfo?.token]);

    const initDashboard = async () => {
        setIsLoading(true);
        const isTokenValid = await checkAuthTokenValid();
        if (!isTokenValid) {
            setIsLoading(false);
            return;
        }

        await Promise.all([
            fetchDashboardData(),
            fetchGenresData(),
            fetchBooksCount(values)
        ]);
        setLastUpdated(new Date());
        setIsLoading(false);
    };

    const handleRefresh = async () => {
        hasFetched.current = false;
        await initDashboard();
        hasFetched.current = true;
    };

    const checkAuthTokenValid = async () => {
        try {
            const { data: response } = await apiUrl.post("/api/admin/check-auth-token");
            if (!response.status && response.isTokenExpired === true) {
                setIsPopupVisible(true);
                return false;
            }
            return true;
        } catch (error) {
            console.error("Error checking auth token:", error);
            return false;
        }
    };

    const fetchDashboardData = async () => {
        try {
            const { data: response } = await apiUrl.get("/api/admin/dashboard");
            if (response.status) {
                setDashboardData(response.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        }
    };

    const fetchBooksCount = async (payload) => {
        try {
            const { data: response } = await apiUrl.post("/api/admin/books/count", { values: payload });
            if (response.status) {
                setBooksCount(response.data);
            }
        } catch (error) {
            console.error("Error fetching books count:", error);
        }
    };

    const fetchGenresData = async () => {
        try {
            if (!adminInfo?.token) return;
            const genresOptions = await fetchAllGenresByLangOptions("en", adminInfo.token);
            setGenresData(
                genresOptions.map((genre) => ({
                    label: genre.genreName,
                    value: genre.genreId,
                    type: genre.type,
                }))
            );
        } catch (error) {
            console.error("Error fetching genres:", error);
        }
    };

    const handleUserValuesUpdate = ({ name, value }) => {
        setValues((prev) => {
            const updated = { ...prev };
            if (name === "type") {
                updated.type = value;
                updated.category = null;
                updated.isShowType = true;
            } else if (name === "category") {
                updated.category = value ? value : null;
                updated.isShowType = !value;
            } else {
                updated[name] = value;
            }
            fetchBooksCount(updated);
            return updated;
        });
    };

    const cardContents = [
        { label: "Total Users", count: dashboardData.userCount, image: "users", path: "/users" },
        { label: "Books", count: booksCount, image: "books", path: "/books" },
        { label: "Categories", count: dashboardData.categoryCount, image: "category", path: "/genre" },
        { label: "Recommenders", count: dashboardData.recommendersCount, image: "feedback", path: "/recommend" },
        { label: "Favorite Authors", count: dashboardData.favoriteAuthorsCount, image: "authors", path: "/favorite-authors" },
        { label: "Collections", count: dashboardData.collectionsCount, image: "collection", path: "/collections" },
    ];

    const chartData = [
        { label: "Users", value: dashboardData.userCount, color: "#6366f1" },
        { label: "Books", value: booksCount, color: "#10b981" },
        { label: "Categories", value: dashboardData.categoryCount, color: "#f59e0b" },
        { label: "Recommenders", value: dashboardData.recommendersCount, color: "#ef4444" },
        { label: "Authors", value: dashboardData.favoriteAuthorsCount, color: "#8b5cf6" },
        { label: "Collections", value: dashboardData.collectionsCount, color: "#06b6d4" },
    ];
    const maxChartValue = Math.max(...chartData.map((d) => d.value), 1);

    const formatTime = (date) => {
        if (!date) return "";
        return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    };

    return (
        <React.Fragment>
            <div className={styles.dashboard}>
                <header className={styles.header}>
                    <div className={styles.headerMain}>
                        <div className={styles.headerLeft}>
                            <div className={styles.logoBadge}>
                                <LayoutDashboard size={24} strokeWidth={2} />
                            </div>
                            <div>
                                <h1 className={styles.title}>Dashboard</h1>
                            </div>
                        </div>
                        <div className={styles.headerRight}>
                            {lastUpdated && (
                                <span className={styles.lastUpdated}>
                                    Updated {formatTime(lastUpdated)}
                                </span>
                            )}
                            <button
                                type="button"
                                className={styles.refreshBtn}
                                onClick={handleRefresh}
                                disabled={isLoading}
                                aria-label="Refresh dashboard"
                            >
                                <RefreshCw
                                    size={18}
                                    className={isLoading ? styles.spinning : ""}
                                />
                                <span>Refresh</span>
                            </button>
                            <div className={styles.filters}>
                                <select
                                    className={styles.select}
                                    value={values.type}
                                    onChange={(e) =>
                                        handleUserValuesUpdate({ name: "type", value: e.target.value })
                                    }
                                >
                                    <option value="NonFiction">Nonfiction</option>
                                    <option value="Fiction">Fiction</option>
                                </select>
                                <select
                                    className={styles.select}
                                    value={values.category?.categoryId || ""}
                                    onChange={(e) => {
                                        const selectedValue = e.target.value;
                                        const selectedGenre = genresData.find(
                                            (g) => g.value === selectedValue
                                        );
                                        handleUserValuesUpdate({
                                            name: "category",
                                            value: selectedValue
                                                ? {
                                                    categoryName: selectedGenre?.label,
                                                    categoryId: selectedGenre?.value,
                                                }
                                                : null,
                                        });
                                    }}
                                >
                                    <option value="">All Categories</option>
                                    {genresData
                                        .filter((genre) => genre.type === values.type)
                                        .map((genre) => (
                                            <option key={genre.value} value={genre.value}>
                                                {genre.label}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </header>

                {isLoading ? (
                    <div className={styles.loadingState}>
                        <div className={styles.skeletonGrid}>
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className={styles.skeletonCard}>
                                    <div className={styles.skeletonIcon} />
                                    <div className={styles.skeletonCount} />
                                    <div className={styles.skeletonLabel} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className={styles.contentGrid}>
                        <div className={styles.chartSection}>
                            <div className={styles.chartCard}>
                                <h3 className={styles.chartTitle}>Distribution Overview</h3>
                                <p className={styles.chartSubtitle}>
                                    Platform metrics at a glance
                                </p>
                                <div className={styles.chartBars}>
                                    {chartData.map((item, index) => (
                                        <div
                                            key={item.label}
                                            className={styles.chartBarRow}
                                            style={{ animationDelay: `${index * 80}ms` }}
                                        >
                                            <span className={styles.chartBarLabel}>{item.label}</span>
                                            <div className={styles.chartBarTrack}>
                                                <div
                                                    className={styles.chartBarFill}
                                                    style={{
                                                        width: `${(item.value / maxChartValue) * 100}%`,
                                                        backgroundColor: item.color,
                                                    }}
                                                />
                                            </div>
                                            <span className={styles.chartBarValue}>{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={styles.cardsSection}>
                            {cardContents.map((card) => (
                                <div
                                    key={card.label}
                                    className={styles.cardWrapper}
                                    onClick={() => navigate(card.path)}
                                >
                                    <Card label={card.label} count={card.count} image={card.image} variant="dark" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <SessionTimeoutPopup
                isVisible={isPopupVisible}
                onClose={() => setIsPopupVisible(false)}
            />
        </React.Fragment>
    );
}
